import fileUploadService from './api/fileUploadService';
import { validateFile, validateFiles, FILE_VALIDATION_CONFIG } from '../utils/fileValidation';
import logger from '../utils/logger';

// Create logger instance for this service
const uploadLogger = logger.create('BatchFileUploadService');

/**
 * Uploads a file to the server using the server-side upload flow with retry capability
 * @param {File} file - The file object to upload
 * @param {String} documentType - Type of document (personal_photo, cv, security_clearance)
 * @param {String} nominationId - Optional nomination ID
 * @param {Number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} - The uploaded file data including id and url
 */
const uploadFile = async (file, documentType, nominationId = null, maxRetries = FILE_VALIDATION_CONFIG.MAX_RETRIES, onProgress = null) => {
  let retries = 0;
  
  // Validate file before upload
  try {
    validateFile(file);
    uploadLogger.uploadEvent('VALIDATION_SUCCESS', file.name, { 
      size: file.size, 
      type: file.type 
    });
  } catch (error) {
    uploadLogger.error('File validation failed', { 
      filename: file.name, 
      error: error.message 
    });
    throw error;
  }
  
  const attemptUpload = async () => {
    try {
      if (onProgress) {
        onProgress({
          fileName: file.name,
          status: 'uploading',
          progress: 0,
          attempt: retries + 1,
          maxRetries
        });
      }
      
      uploadLogger.uploadEvent('UPLOAD_START', file.name, { 
        attempt: retries + 1, 
        nominationId 
      });
      
      // Upload the file directly to the backend using fileUploadService
      const response = await fileUploadService.uploadFile(file, documentType, nominationId);
      
      uploadLogger.uploadEvent('UPLOAD_SUCCESS', file.name, { 
        fileId: response.data.id,
        attempt: retries + 1
      });
      
      if (onProgress) {
        onProgress({
          fileName: file.name,
          status: 'completed',
          progress: 100
        });
      }
      
      return {
        id: response.data.id,
        url: response.data.url,
        filename: response.data.filename
      };
    } catch (error) {
      uploadLogger.error(`File upload failed (attempt ${retries + 1}/${maxRetries})`, {
        filename: file.name,
        error: error.message,
        status: error.response?.status,
        attempt: retries + 1
      });
      
      if (retries < maxRetries) {
        retries++;
        
        if (onProgress) {
          onProgress({
            fileName: file.name,
            status: 'retrying',
            progress: 0,
            attempt: retries,
            maxRetries,
            error: error.message
          });
        }
        
        // Exponential backoff: wait longer between each retry
        const delay = 1000 * Math.pow(2, retries);
        uploadLogger.info(`Retrying upload after ${delay}ms delay`, { 
          filename: file.name, 
          attempt: retries 
        });
        
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Try again
        return attemptUpload();
      }
      
      if (onProgress) {
        onProgress({
          fileName: file.name,
          status: 'failed',
          error: error.message || FILE_VALIDATION_CONFIG.ERROR_MESSAGES.UPLOAD_FAILED
        });
      }
      
      // Categorize error for better user feedback
      let userFriendlyMessage = FILE_VALIDATION_CONFIG.ERROR_MESSAGES.UPLOAD_FAILED;
      
      if (error.code === 'NETWORK_ERROR' || error.name === 'NetworkError') {
        userFriendlyMessage = FILE_VALIDATION_CONFIG.ERROR_MESSAGES.NETWORK_ERROR;
      } else if (error.response?.status >= 500) {
        userFriendlyMessage = FILE_VALIDATION_CONFIG.ERROR_MESSAGES.SERVER_ERROR;
      } else if (error.response?.data?.detail) {
        userFriendlyMessage = error.response.data.detail;
      }
      
      const uploadError = new Error(userFriendlyMessage);
      uploadError.originalError = error;
      uploadError.filename = file.name;
      uploadError.retries = retries;
      
      throw uploadError;
    }
  };
  
  return attemptUpload();
};

/**
 * Uploads multiple files in sequence or batch
 * @param {Object} files - Object containing files to upload with keys as file types
 * @param {String} nominationId - Optional nomination ID
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} - Object with the same keys but containing uploaded file data
 */
const uploadFiles = async (files, nominationId = null, onProgress = null) => {
  const uploadedFiles = {};
  const fileEntries = Object.entries(files).filter(([_, file]) => file && file.file);
  
  if (fileEntries.length === 0) {
    uploadLogger.warn('No files to upload');
    return uploadedFiles;
  }
  
  // Validate all files before starting upload
  try {
    const filesToValidate = fileEntries.map(([_, fileData]) => fileData.file);
    validateFiles(filesToValidate);
    uploadLogger.info(`Starting batch upload of ${fileEntries.length} files`, { 
      nominationId,
      fileTypes: fileEntries.map(([type]) => type)
    });
  } catch (error) {
    uploadLogger.error('Batch file validation failed', { error: error.message });
    throw error;
  }
  
  const totalFiles = fileEntries.length;
  let completedFiles = 0;
  
  const updateOverallProgress = (status) => {
    if (onProgress) {
      onProgress({
        totalFiles,
        completedFiles,
        overallProgress: Math.round((completedFiles / totalFiles) * 100),
        status
      });
    }
  };
  
  updateOverallProgress('starting');
  
  // Try batch upload first if we have multiple files
  if (fileEntries.length > 1) {
    try {
      updateOverallProgress('uploading');
      uploadLogger.info('Attempting batch upload', { fileCount: fileEntries.length });
      
      const filesToUpload = fileEntries.map(([_, fileData]) => fileData.file);
      const response = await fileUploadService.uploadBatch(filesToUpload, nominationId);
      
      uploadLogger.uploadEvent('BATCH_UPLOAD_SUCCESS', `${fileEntries.length} files`, {
        fileIds: response.data.map(f => f.id)
      });
      
      // Map the response back to the original file types
      fileEntries.forEach(([fileType, fileData], index) => {
        if (response.data[index]) {
          uploadedFiles[fileType] = {
            id: response.data[index].id,
            url: response.data[index].url,
            name: response.data[index].filename
          };
        }
      });
      
      completedFiles = totalFiles;
      updateOverallProgress('completed');
      
      return uploadedFiles;
    } catch (error) {
      uploadLogger.warn('Batch upload failed, falling back to sequential uploads', { 
        error: error.message 
      });
      updateOverallProgress('fallback');
    }
  }
  
  // Sequential upload (one by one)
  uploadLogger.info('Starting sequential file upload', { fileCount: fileEntries.length });
  
  for (const [fileType, fileData] of fileEntries) {
    try {
      // Snapshot current completed count to avoid capturing a changing variable in the handler (no-loop-func)
      const baseCompleted = completedFiles;
      const progressHandler = (fileProgress) => {
        if (onProgress) {
          onProgress({
            ...fileProgress,
            fileType,
            totalFiles,
            completedFiles: baseCompleted,
            overallProgress: Math.round(((baseCompleted + (fileProgress.progress / 100)) / totalFiles) * 100)
          });
        }
      };

      const result = await uploadFile(
        fileData.file, 
        fileType, // Pass fileType as documentType
        nominationId, 
        FILE_VALIDATION_CONFIG.MAX_RETRIES,
        progressHandler
      );
      
      uploadedFiles[fileType] = {
        id: result.id,
        url: result.url,
        name: result.filename
      };
      
      completedFiles++;
      updateOverallProgress('uploading');
      
      uploadLogger.uploadEvent('SEQUENTIAL_UPLOAD_SUCCESS', fileData.file.name, { 
        fileType,
        completedFiles,
        totalFiles
      });
      
    } catch (error) {
      uploadLogger.error(`Sequential upload failed for ${fileType}`, {
        filename: fileData.file.name,
        error: error.message
      });
      
      // Create a more descriptive error message
      const errorMessage = `فشل في رفع ملف ${fileType}: ${error.message || FILE_VALIDATION_CONFIG.ERROR_MESSAGES.UPLOAD_FAILED}`;
      const uploadError = new Error(errorMessage);
      uploadError.originalError = error;
      uploadError.fileType = fileType;
      uploadError.filename = fileData.file.name;
      
      throw uploadError;
    }
  }
  
  updateOverallProgress('completed');
  uploadLogger.info('All files uploaded successfully', { 
    totalFiles,
    uploadedFileTypes: Object.keys(uploadedFiles)
  });
  
  return uploadedFiles;
};

const batchFileUploadService = {
  uploadFile,
  uploadFiles
};

export default batchFileUploadService;
